import { useEffect, useRef, useState, useCallback } from 'react';

const timeout = 5 * 60 * 1000; // 5 minutes
const events = [
  'scroll',
  'touchstart',
  'click',
  'mousedown',
  'mousemove',
  'keypress',
  'keydown',
  'input',
  'focus',
  'blur',
];

export const useInactivityTimer = (onTimeout: () => void): number => {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const callbackRef = useRef(onTimeout);
  const timeoutValueRef = useRef(timeout);
  const startTimeRef = useRef<number>(Date.now());

  const [timeRemaining, setTimeRemaining] = useState<number>(timeout);

  useEffect(() => {
    callbackRef.current = onTimeout;
    timeoutValueRef.current = timeout;
  }, [onTimeout, timeout]);

  const clearAllTimers = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  const startTimer = useCallback(() => {
    const currentTimeout = timeoutValueRef.current;
    clearAllTimers();

    const now = Date.now();
    startTimeRef.current = now;
    setTimeRemaining(currentTimeout);

    timeoutRef.current = setTimeout(() => {
      callbackRef.current();
    }, currentTimeout);

    intervalRef.current = setInterval(() => {
      const elapsed = Date.now() - startTimeRef.current;
      const remaining = Math.max(0, currentTimeout - elapsed);

      setTimeRemaining(remaining);

      if (remaining <= 0) {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      }
    }, 5000);
  }, []);

  useEffect(() => {
    startTimer();

    const handleUserActivity = () => {
      startTimer();
    };

    events.forEach((event) => {
      document.addEventListener(event, handleUserActivity, true);
    });

    return () => {
      events.forEach((event) => {
        document.removeEventListener(event, handleUserActivity, true);
      });
      clearAllTimers();
    };
  }, []);

  useEffect(() => {
    if (timeoutValueRef.current !== timeout) {
      startTimer();
    }
  }, [startTimer]);

  const minutes = Math.ceil(timeRemaining / (60 * 1000));

  return minutes;
};
